import React, { useState, useEffect } from 'react';
/* global axios */
import api from '../services/apiService';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Modal from 'react-modal';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CreateTourForm from './CreateTourForm';
import { Helmet } from 'react-helmet';  // Import von React Helmet
import 'moment/locale/de';
import { registerLocale } from 'react-datepicker';
import de from 'date-fns/locale/de';

moment.locale('de');

registerLocale('de', de);

  // Hilfsfunktion, um nur "HH:mm" zu verwenden (falls nötig)
  const formatTimeWithoutSeconds = timeStr =>
  timeStr && timeStr.length >= 5
    ? timeStr.substring(0, 5)
    : null;

// Hilfskomponente für Droppable Container
const DroppableContainer = ({ id, style, children }) => {
  const { setNodeRef } = useDroppable({ id });
  return <div ref={setNodeRef} style={style}>{children}</div>;
};


const isDriverAvailable = (driver, date) => {
  // Falls keine Abwesenheiten vorhanden sind, ist der Fahrer verfügbar.
  if (!driver.absences || driver.absences.length === 0) return true;
  
  const selectedDate = moment(date).startOf('day');
  
  // Prüfe, ob das ausgewählte Datum in irgendeiner Abwesenheit (Urlaub oder Krank) liegt.
  return !driver.absences.some(absence => {
    const absenceStart = moment(absence.startDate).startOf('day');
    const absenceEnd = moment(absence.endDate).endOf('day');
    return (absence.type === 'urlaub' || absence.type === 'krank') &&
           selectedDate.isBetween(absenceStart, absenceEnd, null, '[]');
  });
};


const modalStyles = {
  overlay: { backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1000 },
  content: { maxWidth: '600px', margin: 'auto', padding: '20px', zIndex: 1001 }
};




// Einzelne sortierbare Komponente für einen Transportmarker
const SortableItem = ({ item, onClick, draggable }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item.uniqueId,
    disabled: false,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: '8px',
    padding: '10px',
    border: '1px solid #000',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: draggable ? 'grab' : 'default',
  };

// Wähle je nach relevantType ein Symbol aus
const symbol = item.relevantType === 'delivery' ? '→' : '←';


  // Zeitanzeige: Zeige die relevante Zeit, falls vorhanden
  // Zeitanzeige: gekürzt auf "HH:mm"
  const rawTime = item.relevantType === 'delivery'
    ? item.deliveryTime
    : item.pickupTime;
  const timeToShow = formatTimeWithoutSeconds(rawTime) || 'Keine Angabe';

   return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onClick(item)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Zeit vor dem Pfeil */}
        <span style={{ marginRight: '5px', fontWeight: 200 }}>
          {timeToShow || 'Keine Angabe'}
        </span>
        <strong style={{ fontWeight: 900, fontSize: '1.5em', marginRight: '5px' }}>
          {symbol}
        </strong>
        <span>
          {item.orderItem && item.orderItem.item && item.orderItem.item.name}
        </span>
      </div>

      

      <div style={{ textAlign: 'center' }}>
        {item.relevantType === 'delivery'
          ? (item.operationLocationDelivery || 'Keine Angabe')
          : (item.operationLocationPickup || 'Keine Angabe')}
      </div>
      {/* --- HIER DATUM EINFÜGEN --- */}
      <div style={{ textAlign: 'center', fontSize: 12, color: '#555' }}>
        {item.relevantType === 'delivery'
          ? `Lieferdatum: ${item.delivery ? moment(item.delivery).format('DD.MM.YYYY') : 'Keine Angabe'}`
          : `Abholdatum: ${item.pickup ? moment(item.pickup).format('DD.MM.YYYY') : 'Keine Angabe'}`
        }
      </div>
    </div>
  );
};



// Modal zum Bearbeiten der Zeitfelder (über die Detailansicht)
// Es wird nur das jeweils relevante Feld angezeigt
const EditMarkerDetailsModal = ({ 
   marker, 
   onClose, 
   onMarkerUpdated,
   vehicles,           // neu: Liste aller Fahrzeuge
   toursByVehicle,     // neu: Objekt { [vehicleId]: Tour[] }
   selectedDate        // neu: damit wir Touren filtern können
 }) => {
  const isDelivery = marker.relevantType === 'delivery';
  // Initialisiere den State – wir erwarten das Format "HH:mm"
  const [time, setTime] = useState(
    // null → "" fürs Controlled Input
    formatTimeWithoutSeconds(marker[isDelivery ? 'deliveryTime' : 'pickupTime']) ?? ""
  );

  // 🆕 State für das Datum
const [date, setDate] = useState(
  isDelivery
    ? marker.delivery
      ? new Date(marker.delivery)
      : new Date()
    : marker.pickup
      ? new Date(marker.pickup)
      : new Date()
);
  // Initiale Tour-Id je nach Typ
 const initialTourId = isDelivery
   ? marker.assignedDelivery
   : marker.assignedPickup;
 // Initiales Fahrzeug, in dem diese Tour liegt
 const initialVehicleId = Object.entries(toursByVehicle).find(
   ([vehId, list]) => list.some(t => t.id === initialTourId)
 )?.[0] || "";
 const [vehicleId, setVehicleId] = useState(initialVehicleId);
 const [tourId,       setTourId]       = useState(initialTourId || "");



  const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    const formattedTime = formatTimeWithoutSeconds(time);
    const formattedDate = date ? moment(date).format('YYYY-MM-DD') : null;

    let payload;
    if (isDelivery) {
      payload = {
        deliveryTime: formattedTime,
        delivery: formattedDate,
      };
    } else {
      payload = {
        pickupTime: formattedTime,
        pickup: formattedDate,
      };
    }

    const response = await api.put(`/transportmarkers/${marker.id}`, payload);

    // 2) Zuweisung an Tour/Fahrzeug (wie gehabt)
    if (tourId) {
      await api.put(`/transportmarkers/${marker.id}/assign`, {
        assignmentType: marker.relevantType,
        transportId: tourId,
      });
    } else {
      await api.put(`/transportmarkers/${marker.id}/unassign`, {
        assignmentType: marker.relevantType,
      });
    }
    onMarkerUpdated(response.data);
    onClose();
  } catch (error) {
    console.error('Fehler beim Aktualisieren des Markers:', error);
  }
};

   // Öffnet den Transportmarker in einem neuen Tab, sofern die Auftrags-ID vorhanden ist.
   const jumpToTransportMarker = () => {
    if (marker.orderItem && marker.orderItem.order && marker.orderItem.order.id) {
      // Verwende window.location.origin statt "http://localhost:3000"
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}/orders/${marker.orderItem.order.id}/transportmarkers`, '_blank');
    } else {
      console.error("Keine Auftrags-ID verfügbar.");
    }
  };

  return (
    <Modal
      isOpen={true}
      onRequestClose={onClose}
      contentLabel="Marker Details bearbeiten"
      ariaHideApp={false}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1000 },
        content: { maxWidth: '600px', margin: 'auto', padding: '20px', zIndex: 1001 }
      }}
    >
      <h2>Marker Details bearbeiten</h2>
      {/* Abschnitt mit den Transportmarkerdetails */}
      <div style={{ marginBottom: '20px', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
      
        <h3>Transport Details</h3>
        {marker.name && <p><strong>{marker.name}</strong></p>}
        {isDelivery ? (
          <>
            <p>Delivery: {moment(marker.delivery).format('DD.MM.YYYY') }</p>
            <p>Delivery Location: {marker.operationLocationDelivery}</p>
            {/* NEU: Bestell-Lieferzeit anzeigen */}
        {marker.orderItem?.order?.deliveryTime && (
          <p>
    Lieferzeit gem. Auftrag:&nbsp;
    {formatTimeWithoutSeconds(marker.orderItem.order.deliveryTime) || '–:–'}
  </p>
        )}
          </>
        ) : (
          <>
            <p>Pickup: {moment(marker.pickup).format('DD.MM.YYYY') }</p>
            <p>Pickup Location: {marker.operationLocationPickup}</p>
          </>
        )}
        {marker.transportNotes && <p>Notes: {marker.transportNotes}</p>}
        {marker.orderItem && marker.orderItem.item && (
          <p>Gerät: {marker.orderItem.item.name}</p>
        )}
        {marker.orderItem && marker.orderItem.order && (
          <>
            <p>
              Auftragsnummer: {marker.orderItem.order.orderNumber || 'Keine Angabe'}
            </p>
            <p>
              Kunde: {marker.orderItem.order.customer
                ? marker.orderItem.order.customer.company
                : 'Keine Angabe'}
            </p>
            <p>
              Ansprechpartner vor Ort: {marker.orderItem.order.lwOperator
                ? `${marker.orderItem.order.lwOperator.contactFirstName} ${marker.orderItem.order.lwOperator.contactLastName}, ${marker.orderItem.order.lwOperator.contactMobile}, ${marker.orderItem.order.lwOperator.contactPhone}`
                : 'Nicht zugeordnet'}
            </p>
            <p>
              Notitz: {marker.transportNotes || 'Keine Angabe'}
            </p>
          </>
        )}
      </div>

      {/* NEU: Fahrzeug wählen */}
      <div style={{ margin: '10px 0' }}>
  <label>Fahrzeug:</label>
  <select
    value={vehicleId}
    onChange={e => {
    setVehicleId(e.target.value);
    if (!e.target.value) {
      setTourId(""); // Tour automatisch auf leer setzen!
    }
  }}
    style={{ width: '100%', padding: '8px' }}
  >
    <option value="">– kein Fahrzeug –</option>
    {vehicles.map(v => {
      const hasTourAtDate =
        (toursByVehicle[v.id] || []).some(
          tour => moment(tour.scheduledDate).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')
        );
      return (
        <option key={v.id} value={v.id} disabled={!hasTourAtDate}>
          {v.name} {v.licensePlate && `(${v.licensePlate})`}
          {!hasTourAtDate ? ' (keine Tour oder Datum anders)' : ''}
        </option>
      );
    })}
  </select>
</div>

      {/* NEU: Tour wählen (abhängig von vehicleId UND Datum) */}
<div style={{ margin: '10px 0' }}>
  <label>Tour:</label>
  <select
    value={tourId}
    onChange={e => setTourId(e.target.value)}
    style={{ width: '100%', padding: '8px' }}
    disabled={!vehicleId}
  >
    <option value="">– keine Tour –</option>
    {(toursByVehicle[vehicleId] || [])
      .filter(tour =>
        // Nur Touren am relevanten Datum anzeigen!
        moment(tour.scheduledDate).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')
      )
      .map(t => (
        <option key={t.id} value={t.id}>
          {t.tourName || '– ohne Name –'} (
            {formatTimeWithoutSeconds(t.timeframeFrom) || '--:--'}–
            {formatTimeWithoutSeconds(t.timeframeTo)   || '--:--'}
          )
        </option>
      ))}
  </select>
</div>

      {/* Formular für die Zeiteingabe */}
      <form onSubmit={handleSubmit}>
        {isDelivery ? (
    <div style={{ marginBottom: '10px' }}>
      <label style={{ display: 'block', marginBottom: '5px',  }}>Lieferdatum:</label>
      <DatePicker
        selected={date}
        onChange={d => setDate(d)}
        dateFormat="dd.MM.yyyy"
        locale="de"
        required
         popperClassName="datepicker-z-fix"
      />
    </div>
  ) : (
    <div style={{ marginBottom: '10px' }}>
      <label style={{ display: 'block', marginBottom: '5px',  }}>Abholdatum:</label>
      <DatePicker
        selected={date}
        onChange={d => setDate(d)}
        dateFormat="dd.MM.yyyy"
        locale="de"
        required
         popperClassName="datepicker-z-fix"
      />
    </div>
  )}
  {/* Zeitfeld wie gehabt */}
  <div style={{ marginBottom: '10px' }}>
    <label style={{ display: 'block', marginBottom: '5px' }}>
      {isDelivery ? 'Delivery Time:' : 'Pickup Time:'}
    </label>
    <input
      type="time"
      step="60"
      value={time}
      onChange={e => setTime(e.target.value)}
      
    />
  </div>
  <button type="submit">Speichern</button>
  <button type="button" onClick={onClose} style={{ marginLeft: '0px' }}>Abbrechen</button>
</form>
      {/* Neuer Button, um in den Transportmarker zu springen */}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <button onClick={jumpToTransportMarker} style={{ padding: '10px 20px', cursor: 'pointer' }}>
          Zum Transportmarker
        </button>
      </div>
    </Modal>
  );
};




// Modal zum Bearbeiten der Zeit beim Zuweisen (vom unassigned-Bereich in ein Tour-Feld)
const EditTimeModal = ({ pending, onClose, onTimeAssigned }) => {
  // pending enthält: { marker, tourId }
  const { marker, tourId } = pending;
  const isDelivery = marker.relevantType === 'delivery';
  // Initialisiere mit vorhandenem Wert oder leeren String
  const [time, setTime] = useState(marker[isDelivery ? 'deliveryTime' : 'pickupTime'] || "");

  // Helper-Funktion, um Sekunden zu entfernen
  const formatTimeWithoutSeconds = (timeStr) => {
    return timeStr && timeStr.length >= 5 ? timeStr.substring(0,5) : timeStr;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formattedTime = formatTimeWithoutSeconds(time);
      await api.put(`/transportmarkers/${marker.id}`, 
        isDelivery ? { deliveryTime: formattedTime } : { pickupTime: formattedTime }
      );
      await api.put(`/transportmarkers/${marker.id}/assign`, {
        assignmentType: marker.relevantType,
        transportId: tourId,
      });
      console.log(`Marker ${marker.id} assigned to tour ${tourId} with time ${formattedTime}`);
      onTimeAssigned(marker.id, tourId, formattedTime);
      onClose();
    } catch (error) {
      console.error('Fehler bei der Zeitzuordnung:', error);
    }
  };

  return (
    <Modal
      isOpen={true}
      onRequestClose={onClose}
      contentLabel="Zeit anpassen"
      ariaHideApp={false}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1000 },
        content: { maxWidth: '400px', margin: 'auto', padding: '20px', zIndex: 1001 }
      }}
    >
      <h3>{isDelivery ? 'Delivery Time' : 'Pickup Time'} anpassen</h3>
      <div style={{ marginBottom: '20px' }}>
        <input 
          type="time" 
          value={time} 
          step="60"
          onChange={(e) => {
            console.log("Zeit im Modal geändert:", e.target.value);
            setTime(e.target.value);
          }}
          required
        />
      </div>
      <button onClick={handleSubmit}>Speichern</button>
      <button onClick={onClose} style={{ marginLeft: '10px' }}>Abbrechen</button>
    </Modal>
  );
};




const EditTourForm = ({ tour, onClose, onTourUpdated, drivers ,selectedDate, onTourDeleted = () => {} }) => {
  const [tourName, setTourName] = useState(tour.tourName || '');
  const [scheduledDate, setScheduledDate] = useState(new Date(tour.scheduledDate));
  const [timeframeFrom, setTimeframeFrom] = useState(
    formatTimeWithoutSeconds(tour.timeframeFrom) ?? ''
  );
  const [timeframeTo, setTimeframeTo] = useState(
    formatTimeWithoutSeconds(tour.timeframeTo) ?? ''
  );
  const [driverId, setDriverId] = useState(tour.driverId || '');
  const [notes, setNotes] = useState(tour.notes || '');
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        tourName,
        scheduledDate: moment(scheduledDate).format('YYYY-MM-DD'),
        timeframeFrom: timeframeFrom || null,
        timeframeTo: timeframeTo   || null,
        driverId: driverId || null,
        notes,
      };
      const response = await api.put(`/transports/${tour.id}`, payload);
      onTourUpdated(response.data);
      onClose();
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Tour:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Möchten Sie diese Tour wirklich löschen?")) {
      try {
        await api.delete(`/transports/${tour.id}`);
        onTourDeleted(tour.id);
        onClose();
      } catch (error) {
        console.error('Fehler beim Löschen der Tour:', error);
      }
    }
  };


  return (
    <div>
    <h3>Tour bearbeiten</h3>
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '0px' }}>
        <label style={{ display: 'block', marginBottom: '5px' }}>Tourname:</label>
        <input 
          type="text" 
          value={tourName} 
          onChange={(e) => setTourName(e.target.value)} 
          required 
        />
      </div>
      
      <div style={{ marginBottom: '0px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Zeitraum von:</label>
          <input 
            type="time" 
            step="60"
            value={timeframeFrom} 
            onChange={(e) => setTimeframeFrom(e.target.value)}
            required 
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Zeitraum bis:</label>
          <input 
            type="time" 
            step="60"
            value={timeframeTo} 
            onChange={(e) => setTimeframeTo(e.target.value)}
            required 
          />
        </div>
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', marginBottom: '5px' }}>Fahrer (optional):</label>
        <select 
          value={driverId} 
          onChange={(e) => setDriverId(e.target.value)}
          style={{
            padding: '10px',
            borderRadius: '0px',
            border: '1px solid #ccc',
            width: '250px',
            marginLeft:  '0px',
          }}
        >
          <option value="">-- Fahrer wählen --</option>
          {drivers.filter(driver => driver.role === 'Fahrer' && isDriverAvailable(driver, selectedDate)).map(driver => (
            <option key={driver.id} value={driver.id}>
              {driver.firstName} {driver.lastName}
            </option>
          ))}
        </select>
      </div>
      <button 
          type="button" 
          onClick={handleDelete} 
          style={{ marginLeft: '0px', backgroundColor: '#e74c3c', color: '#fff' }}
        >
          Tour löschen
        </button>
      <button type="submit">Tour speichern</button>
      <button type="button" onClick={onClose}>Abbrechen</button>
      
    </form>
  </div>
);
};

const TransportForm = () => {
  // Initialisiere selectedDate mit dem Wert aus dem Local Storage, falls vorhanden
  const [selectedDate, setSelectedDate] = useState(() => {
    const stored = localStorage.getItem('selectedDate');
    return stored ? new Date(stored) : new Date();
  });
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  // containers: globaler "unassigned"-Bereich und Container für Marker, die einer Tour zugeordnet sind (Key: "tour-<tourId>")
  const [containers, setContainers] = useState({ unassigned: [] });
  const [selectedTransport, setSelectedTransport] = useState(null);
  // tours: Mapping von Fahrzeug-ID -> Array von Touren
  const [tours, setTours] = useState({});
  // Zustände für Tour-Erstellung/Editing
  const [creatingTourFor, setCreatingTourFor] = useState(null);
  const [editingTour, setEditingTour] = useState(null);
  // Dropdown-Zustand je Tour (tour.id -> boolean, true = ausgeklappt)
  const [dropdownState, setDropdownState] = useState({});
  // Pending Assignment für Zeit-Edit (Drag aus unassigned in Tour-Feld)
  const [pendingAssignment, setPendingAssignment] = useState(null);
  // Marker, die in der Detailansicht editiert werden sollen
  const [editingMarker, setEditingMarker] = useState(null);

  const [unassignedFrom, setUnassignedFrom] = useState(() => {
  const stored = localStorage.getItem('unassignedFrom');
  return stored ? new Date(stored) : new Date();
});
const [unassignedTo, setUnassignedTo] = useState(() => {
  const stored = localStorage.getItem('unassignedTo');
  return stored ? new Date(stored) : new Date();
});
useEffect(() => {
  localStorage.setItem('unassignedFrom', unassignedFrom.toISOString());
}, [unassignedFrom]);
useEffect(() => {
  localStorage.setItem('unassignedTo', unassignedTo.toISOString());
}, [unassignedTo]);


  // Speichere selectedDate bei jeder Änderung im Local Storage
  useEffect(() => {
    localStorage.setItem('selectedDate', selectedDate.toISOString());
  }, [selectedDate]);

  // Schließfunktion für das Bearbeiten eines Markers
  const closeModal = () => setEditingMarker(null);

  const toggleDropdown = (tourId) => {
    setDropdownState(prev => ({ ...prev, [tourId]: !prev[tourId] }));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    // ggf. neu laden...
  };

  // Fahrer laden
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const response = await api.get('/employees');
        setDrivers(response.data);
      } catch (error) {
        console.error('Fehler beim Abrufen der Fahrer:', error);
      }
    };
    fetchDrivers();
  }, []);
  

  // Fahrzeuge laden
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await api.get('/items');
        const vehiclesData = response.data.filter(item => item.itemType === 'transportvehicle');
        setVehicles(vehiclesData);
        const vehicleContainers = {};
        vehiclesData.forEach(vehicle => {
          vehicleContainers[String(vehicle.id)] = [];
        });
        setContainers(prev => ({ ...prev, ...vehicleContainers }));
      } catch (error) {
        console.error('Fehler beim Abrufen der Fahrzeuge:', error);
      }
    };
    fetchVehicles();
  }, []);


  // ⬇️ Neu: Datums-Range inklusiv prüfen (lokal, TZ-sicher)
const isWithinRange = (dateStr, fromDate, toDate) => {
  if (!dateStr) return false;
  const d   = moment(dateStr, ['YYYY-MM-DD', moment.ISO_8601]).startOf('day');
  const from = moment(fromDate).startOf('day');
  const to   = moment(toDate).endOf('day');
  return d.isSameOrAfter(from) && d.isSameOrBefore(to);
};

// ⬇️ Neu: sichere Datumssortierung (lokal)
const compareByRelevantDate = (a, b) => {
  const da = a.relevantType === 'delivery' ? a.delivery : a.pickup;
  const db = b.relevantType === 'delivery' ? b.delivery : b.pickup;
  if (!da && !db) return 0;
  if (!da) return 1;
  if (!db) return -1;
  const ma = moment(da, ['YYYY-MM-DD', moment.ISO_8601]).valueOf();
  const mb = moment(db, ['YYYY-MM-DD', moment.ISO_8601]).valueOf();
  return ma - mb;
};
  // Transportmarker laden und verteilen
useEffect(() => {
  const fetchMarkers = async () => {
    try {
      // Unzugewiesene Marker im Zeitraum holen!
      const responseUnassigned = await api.get('/transportmarkers/unassigned', {
        params: {
          from: moment(unassignedFrom).format('YYYY-MM-DD'),
          to: moment(unassignedTo).format('YYYY-MM-DD')
        }
      });
      const unassignedMarkers = responseUnassigned.data;

      // Tour-marker wie gehabt, aber NUR für das aktuelle Tagesdatum:
      const responseAll = await api.get('/transportmarkers', {
        params: { t: new Date().getTime() }
      });
      const allMarkers = responseAll.data;
      const formattedDate = moment(selectedDate).format('YYYY-MM-DD');

      // Container aufbauen
      let newContainers = { unassigned: [] };
      vehicles.forEach(vehicle => {
        newContainers[String(vehicle.id)] = [];
      });

      // Unzugewiesene aus dem Zeitraum einsortieren
      // Unzugewiesene aus dem Zeitraum einsortieren (mit zusätzlichem Frontend-Filter)
unassignedMarkers.forEach(marker => {
  const baseId = String(marker.id);

  // DELIVERY-Seite nur, wenn unassigned + Datum vorhanden + im Range
  if (
    marker.assignedDelivery === null &&
    marker.delivery &&
    isWithinRange(marker.delivery, unassignedFrom, unassignedTo)
  ) {
    newContainers.unassigned.push({
      ...marker,
      baseId,
      relevantType: 'delivery',
      uniqueId: `${baseId}-delivery`,
    });
  }

  // PICKUP-Seite nur, wenn unassigned + Datum vorhanden + im Range
  if (
    marker.assignedPickup === null &&
    marker.pickup &&
    isWithinRange(marker.pickup, unassignedFrom, unassignedTo)
  ) {
    newContainers.unassigned.push({
      ...marker,
      baseId,
      relevantType: 'pickup',
      uniqueId: `${baseId}-pickup`,
    });
  }
});

// *** Stabil sortieren nach relevantem Datum (lokal) ***
newContainers.unassigned.sort(compareByRelevantDate);


      // *** NEU: Strikte Sortierung NACH DATUM (egal ob delivery oder pickup) ***
newContainers.unassigned.sort((a, b) => {
  const dateA = a.relevantType === "delivery" ? a.delivery : a.pickup;
  const dateB = b.relevantType === "delivery" ? b.delivery : b.pickup;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return new Date(dateA) - new Date(dateB);
});

      // Die zugewiesenen Marker bleiben weiterhin tagesbezogen:
      allMarkers.forEach(marker => {
        const baseId = String(marker.id);
        const markerWithId = { ...marker, baseId };
        const deliveryDate = moment(marker.delivery).format('YYYY-MM-DD');
        const pickupDate = moment(marker.pickup).format('YYYY-MM-DD');
        if (formattedDate === deliveryDate && marker.assignedDelivery !== null) {
          const key = `tour-${marker.assignedDelivery}`;
          if (!newContainers[key]) newContainers[key] = [];
          newContainers[key].push({ ...markerWithId, relevantType: 'delivery', uniqueId: `${baseId}-delivery` });
        }
        if (formattedDate === pickupDate && marker.assignedPickup !== null) {
          const key = `tour-${marker.assignedPickup}`;
          if (!newContainers[key]) newContainers[key] = [];
          newContainers[key].push({ ...markerWithId, relevantType: 'pickup', uniqueId: `${baseId}-pickup` });
        }
      });

      // Tour-Container sortieren wie gehabt
      Object.keys(newContainers).forEach(key => {
        if (key.startsWith("tour-")) {
          newContainers[key].sort((a, b) => {
            const timeA = a.relevantType === "delivery" ? a.deliveryTime : a.pickupTime;
            const timeB = b.relevantType === "delivery" ? b.deliveryTime : b.pickupTime;
            if (!timeA) return 1;
            if (!timeB) return -1;
            return timeA < timeB ? -1 : timeA > timeB ? 1 : 0;
          });
        }
      });

      setContainers(newContainers);
    } catch (error) {
      console.error("Fehler beim Abrufen der Transportmarker:", error);
    }
  };
  fetchMarkers();
}, [selectedDate, vehicles, unassignedFrom, unassignedTo]);

  
  // WorkshopOrders laden (z.B. alle)
const [workshopOrders, setWorkshopOrders] = useState([]);
useEffect(() => {
  const fetchWorkshopOrders = async () => {
    try {
      const response = await api.get('/workshoporders');
      setWorkshopOrders(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der WorkshopOrders:', error);
    }
  };
  fetchWorkshopOrders();
}, []);



  // Touren laden
  useEffect(() => {
    const fetchTours = async () => {
      try {
        const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
        const response = await api.get(`/transports?scheduledDate=${formattedDate}`);
        const tourMapping = {};
        response.data.forEach(tour => {
          const key = String(tour.vehicleId);
          if (!tourMapping[key]) {
            tourMapping[key] = [];
          }
          tourMapping[key].push(tour);
        });
        setTours(tourMapping);
        // Container komplett neu aufbauen: 
        setContainers(prev => {
          // Erstelle ein neues Objekt, das die bisherigen Container enthält:
          const newContainers = { ...prev };
          
          // Für alle Fahrzeuge: Falls der Container noch nicht existiert, initialisieren
          vehicles.forEach(vehicle => {
            if (!newContainers[String(vehicle.id)]) {
              newContainers[String(vehicle.id)] = [];
            }
          });
          
          // Füge für jede Tour einen Container-Key hinzu (überschreibe nicht vorhandene)
          response.data.forEach(tour => {
            const tourKey = `tour-${tour.id}`;
            if (!newContainers[tourKey]) {
              newContainers[tourKey] = [];
            }
          });
          
          return newContainers;
        });
        
      } catch (error) {
        console.error('Fehler beim Abrufen der Touren:', error);
      }
    };
    fetchTours();
  }, [selectedDate, vehicles]);


  useEffect(() => {
    // Nur fortfahren, wenn Touren, Fahrer und Container bereits geladen sind
    if (!drivers || !tours || !containers) return;
  
    // Durchlaufe alle Touren (innerhalb der Fahrzeugzuordnungen)
    Object.keys(tours).forEach(vehicleId => {
      tours[vehicleId].forEach(tour => {
        if (tour.driverId) {
          // Hole den Fahrer aus dem drivers-Array
          const driver = drivers.find(d => d.id === tour.driverId);
          // Falls der Fahrer gefunden wurde und NICHT verfügbar ist
          if (driver && !isDriverAvailable(driver, selectedDate)) {
            const tourContainerKey = `tour-${tour.id}`;
            const markersToUnassign = containers[tourContainerKey] || [];
            markersToUnassign.forEach(marker => {
              // Extrahiere die numerische ID (angenommen, marker.uniqueId ist z. B. "7-delivery")
              const numericId = marker.uniqueId.split('-')[0];
              // Führe API-Aufruf aus, um Marker zu unassignen
              api.put(`/transportmarkers/${numericId}/unassign`, {
                assignmentType: marker.relevantType,
              })
              .then(() => {
                console.log(`Marker ${marker.uniqueId} von Tour ${tour.id} wurde unassigned, da Fahrer nicht verfügbar ist.`);
                // Update des State: Marker aus dem Tour-Container entfernen und in den unassigned-Container verschieben
                setContainers(prev => {
                  const updatedTourContainer = (prev[tourContainerKey] || []).filter(m => m.uniqueId !== marker.uniqueId);
                  return {
                    ...prev,
                    [tourContainerKey]: updatedTourContainer,
                    unassigned: [...(prev.unassigned || []), marker],
                  };
                });
              })
              .catch(error => {
                console.error(`Fehler beim Unassignen von Marker ${marker.uniqueId}:`, error);
              });
            });
          }
        }
      });
    });
  }, [tours, drivers, selectedDate, containers]);
  



  

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Helfer: Ermittelt den Container eines Markers
  const getContainer = (id) => {
    // id ist hier der uniqueId-Wert (z.B. "7-delivery" oder "7-pickup")
    for (const key in containers) {
      if (containers[key] && containers[key].some(item => item.uniqueId === id)) {
        return key;
      }
    }
    // Fallback: Wenn nicht gefunden, gib "unassigned" zurück.
    return 'unassigned';
  };

  // Drag & Drop Handler – nur zulassen in "unassigned" oder Tour-Felder
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    console.log("Drag End Event:", { active, over });
    if (!over) return;
    
    const activeContainer = getContainer(active.id);
    let overContainer = over.id;
    if (overContainer.endsWith('-list')) {
      overContainer = overContainer.slice(0, -5);
    }
    console.log(`Active Container: ${activeContainer} | Over Container: ${overContainer}`);
    if (!activeContainer || !overContainer) return;
    if (!(overContainer === 'unassigned' || overContainer.startsWith('tour-'))) {
      console.warn(`Ungültiges Drop-Ziel: ${overContainer}`);
      return;
    }
    if (!(overContainer in containers)) {
      setContainers(prev => ({ ...prev, [overContainer]: [] }));
      console.warn(`Container ${overContainer} wurde neu angelegt.`);
      return;
    }
    
    if (activeContainer === overContainer) {
      const items = containers[activeContainer];
      const oldIndex = items.findIndex(item => item.uniqueId === active.id);
      let newIndex = items.findIndex(item => item.uniqueId === over.id);
      if (newIndex === -1) {
        newIndex = items.length;
      }
      console.log(`Innerhalb des gleichen Containers: Alte Index: ${oldIndex}, Neue Index: ${newIndex}`);
      if (oldIndex !== newIndex) {
        setContainers({
          ...containers,
          [activeContainer]: arrayMove(items, oldIndex, newIndex),
        });
      }
    } else {
      const activeItems = [...containers[activeContainer]];
      const overItems = [...containers[overContainer]] || [];
      const activeIndex = activeItems.findIndex(item => item.uniqueId === active.id);
      if (activeIndex === -1) {
        console.warn(`Item mit id ${active.id} wurde im Container ${activeContainer} nicht gefunden.`);
        return;
      }
      const [movedItem] = activeItems.splice(activeIndex, 1);
      let overIndex = overItems.findIndex(item => item.uniqueId === over.id);
      if (overIndex === -1) {
        overIndex = overItems.length;
      }
      overItems.splice(overIndex, 0, movedItem);
      console.log(`Marker ${movedItem.id} wird von Container ${activeContainer} in ${overContainer} verschoben.`);
      setContainers({
        ...containers,
        [activeContainer]: activeItems,
        [overContainer]: overItems,
      });
      
      // Falls Marker aus unassigned in ein Tour-Feld gezogen wird, öffne Modal zur Zeiteingabe
      if (activeContainer === 'unassigned' && overContainer.startsWith('tour-')) {
        const tourId = overContainer.replace('tour-', '');
        console.log(`Marker ${movedItem.id} wurde aus unassigned in Tour ${tourId} gezogen.`);
        setPendingAssignment({ marker: movedItem, tourId });
        return;
      }
      // Falls Marker aus einem Tour-Feld in unassigned gezogen wird, führe Unassign aus
      else if (activeContainer.startsWith('tour-') && overContainer === 'unassigned') {
        console.log(`Marker ${active.id} wird von Tour ${activeContainer.replace('tour-', '')} zurück in unassigned verschoben.`);
        try {
          // Extrahiere die Basis-ID (numerisch) aus der uniqueId ("7-delivery" -> "7")
          const numericId = active.id.split('-')[0];
          await api.put(`/transportmarkers/${numericId}/unassign`, {
            assignmentType: movedItem.relevantType,
          });
          console.log(`Marker ${active.id} unassigned`);
        } catch (error) {
          console.error('Fehler beim Aufheben der Zuordnung:', error);
        }
      }
      // Verschiebung zwischen Tour-Feldern
      else if (activeContainer.startsWith('tour-') && overContainer.startsWith('tour-') && activeContainer !== overContainer) {
        const tourId = overContainer.replace('tour-', '');
        console.log(`Marker ${active.id} wird zwischen Touren verschoben. Neuer Tour-Schlüssel: ${tourId}`);
        try {
          const numericId = active.id.split('-')[0];
          await api.put(`/transportmarkers/${numericId}/assign`, {
            assignmentType: movedItem.relevantType,
            transportId: tourId,
          });
          console.log(`Marker ${active.id} reassigned to tour ${tourId}`);
        } catch (error) {
          console.error('Fehler beim Aktualisieren der Zuordnung:', error);
        }
      }
    }
  };
  

  const openEditMarkerModal = (item) => 
    setEditingMarker(item);
  const closeEditMarkerModal = () => setEditingMarker(null);

  // Öffnet das CreateTourForm
  const handleOpenCreateTour = (vehicle) => {
    setCreatingTourFor(vehicle);
  };

  // Callback, wenn eine Tour erstellt wurde
  const handleTourCreated = (tour) => {
    setTours(prev => {
      const vehicleKey = String(tour.vehicleId);
      const toursForVehicle = prev[vehicleKey] || [];
      return { ...prev, [vehicleKey]: [...toursForVehicle, tour] };
    });
    setCreatingTourFor(null);
  };

  // Callback, wenn eine Tour aktualisiert wurde
  const handleTourUpdated = (updatedTour) => {
    setTours(prev => {
      const vehicleKey = String(updatedTour.vehicleId);
      const toursForVehicle = prev[vehicleKey] || [];
      const updatedTours = toursForVehicle.map(tour =>
        tour.id === updatedTour.id ? updatedTour : tour
      );
      return { ...prev, [vehicleKey]: updatedTours };
    });
  };

  // Callback, wenn eine Tour gelöscht wurde
  const handleTourDeleted = (deletedTourId) => {
    setTours(prevTours => {
      const updatedTours = {};
      Object.keys(prevTours).forEach(vehicleId => {
        updatedTours[vehicleId] = prevTours[vehicleId].filter(tour => tour.id !== deletedTourId);
      });
      return updatedTours;
    });
  };

  

  // Callback, wenn die Zeit im Marker (über Pending Assignment Modal) editiert wurde
  const handleTimeUpdated = (markerId, tourId, time) => {
    setPendingAssignment(null);
    // Optional: Hier Marker neu laden oder lokalen State aktualisieren.
  };

  // Callback, wenn Marker-Details editiert wurden
  const handleMarkerUpdated = (updatedMarker) => {
   const baseId = String(updatedMarker.id);
   // Neuer Container-Key (je nach Typ)
   const assignedKey = `tour-${updatedMarker.assignedDelivery ?? updatedMarker.assignedPickup}`;

   setContainers(prev => {
     // 1) Unassigned filtern
     const newUnassigned = prev.unassigned.filter(m => m.baseId !== baseId);

     // 2) Marker-Objekt anlegen
     const updatedItem = {
       ...updatedMarker,
       baseId,
       uniqueId: `${baseId}-${updatedMarker.relevantType}`
     };

     // 3) In den Tour-Container hinzufügen
     const newContainers = {
       ...prev,
       unassigned: newUnassigned,
       [assignedKey]: [...(prev[assignedKey] || []), updatedItem],
     };
     return newContainers;
   });
 };

  return (
    <div>
      <h2>Transportdisposition</h2>
      <div style={{ marginBottom: '20px' }}>
      <Helmet>
        <title>Transporte planen</title>
        <meta name="description" content="Transporte planen" />
      </Helmet>
        <label style={{ marginRight: '10px' }}>Tagesdatum wählen:</label>
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat="dd.MM.yyyy"
          locale="de"
        />
      </div>
      
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          
          {/* Unassigned Container */}
          <DroppableContainer id="unassigned" style={{ width: '23%', padding: '10px', border: '1px solid #ccc', minHeight: '300px' }}>
            <h3>Unzugewiesene Aufträge</h3>
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 ,justifyContent: 'center', }}>
  <strong style={{ fontSize: 13 }}></strong>
  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
    <span style={{ fontSize: 13, marginRight: 2 }}></span>
    <DatePicker
      selected={unassignedFrom}
      onChange={setUnassignedFrom}
      dateFormat="dd.MM.yyyy"
      locale="de"
      selectsStart
      startDate={unassignedFrom}
      endDate={unassignedTo}
      maxDate={unassignedTo}
      popperPlacement="bottom-end"
      showPopperArrow={false}
      customInput={
        <input
          style={{
            width: 100,
            padding: '2px 4px',
            fontSize: 13,
            border: '1px solid #bbb',
            borderRadius: 4,
            background: '#f9f9f9',
            marginRight: 0,
          }}
        />
      }
    />
  </span>
  <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
    <span style={{ fontSize: 13, marginRight: 2 }}></span>
    <DatePicker
      selected={unassignedTo}
      onChange={setUnassignedTo}
      dateFormat="dd.MM.yyyy"
      locale="de"
      selectsEnd
      startDate={unassignedFrom}
      endDate={unassignedTo}
      minDate={unassignedFrom}
      popperPlacement="bottom-end"
      showPopperArrow={false}
      customInput={
        <input
          style={{
            width: 100,
            padding: '2px 4px',
            fontSize: 13,
            border: '1px solid #bbb',
            borderRadius: 4,
            background: '#f9f9f9',
            marginRight: 0,
          }}
        />
      }
    />
  </span>
</div>

            <SortableContext items={containers.unassigned.filter(item => item).map(item => item.uniqueId)} strategy={verticalListSortingStrategy}>
  {containers.unassigned.filter(item => item).map(item => (
    <SortableItem key={item.uniqueId} item={item} onClick={openEditMarkerModal} draggable={true} />
  ))}
</SortableContext>

          </DroppableContainer>
          {/* Fahrzeugcontainer */}
          <div style={{ 
  display: 'grid', 
  gridTemplateColumns: 'repeat(5, 1fr)', 
  gap: '0px', 
  width: '100%' 
}}>
    {vehicles
    .filter(vehicle => {
      // Fahrzeug darf nicht deaktiviert sein.
      if (vehicle.deactivated) return false;
      
      // Suche alle WorkshopOrders, die diesem Fahrzeug (itemId === vehicle.id) zugeordnet sind.
      const ordersForVehicle = workshopOrders.filter(order => order.itemId === vehicle.id);
      
      if (ordersForVehicle.length > 0) {
        // Konvertiere das ausgewählte Datum (als Moment-Objekt, Beginn des Tages)
        const selected = moment(selectedDate).startOf('day');
        // Prüfe, ob mindestens eine WorkshopOrder das Datum blockiert.
        const isBlocked = ordersForVehicle.some(order => {
          if (order.blockedPeriodFrom && order.blockedPeriodTo) {
            const blockedFrom = moment(order.blockedPeriodFrom).startOf('day');
            const blockedTo = moment(order.blockedPeriodTo).endOf('day');
            return selected.isBetween(blockedFrom, blockedTo, null, '[]');
          }
          return false;
        });
        if (isBlocked) {
          console.log(`Fahrzeug ${vehicle.name} wird blockiert (WorkshopOrder: ${ordersForVehicle.map(o => o.workshopOrderNumber).join(', ')})`);
          return false;
        }
      }
      
      return true;
    })
  .map(vehicle => {
    return (
      <DroppableContainer 
        key={vehicle.id} 
        id={String(vehicle.id)} 
        style={{ 
          padding: '0px', 
          border: '1px solid #ccc' 
        }}
      >
       <h3 style={{ margin: '0 0 10px 0' }}>
  {vehicle.name} {vehicle.licensePlate && `(${vehicle.licensePlate})`}
</h3>
        {/* Icon für Neue Tour anlegen */}
        <div style={{ marginBottom: '10px' }}>
          <span 
            onClick={() => handleOpenCreateTour(vehicle)}
            style={{ cursor: 'pointer', fontSize: '1.5em', padding: '5px' }}
            title="Neue Tour anlegen"
          >
            +
          </span>
        </div>
        {/* Hier ggf. Touren anzeigen */}
        {tours[String(vehicle.id)] && tours[String(vehicle.id)].length > 0 && (
  <div style={{ marginTop: '10px', background: '#f0f0f0', padding: '5px' }}>
    {tours[String(vehicle.id)].map(tour => (
      <div key={tour.id} style={{ marginTop: '5px', border: '1px dashed #888', padding: '5px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            {tour.tourName || 'Keine Bezeichnung'} (
              {formatTimeWithoutSeconds(tour.timeframeFrom) || '--:--'} – {formatTimeWithoutSeconds(tour.timeframeTo) || '--:--'}
            )
            {tour.driverId && drivers.length > 0 &&
              ` - ${drivers.find(d => d.id === tour.driverId)?.lastName || ''}`
            }
          </span>
          {/* Gemeinsamer flex Container für beide Icons */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span 
              onClick={() => setEditingTour(tour)}
              style={{ cursor: 'pointer', marginRight: '5px' }}
              title="Tour editieren"
            >
              ✎
            </span>
            <span 
              onClick={() => toggleDropdown(tour.id)}
              style={{ cursor: 'pointer', fontSize: '1.2em' }}
              title={dropdownState[tour.id] ? 'Einklappen' : 'Ausklappen'}
            >
              🔍
            </span>
          </div>
        </div>
        {/* Dropdown-Bereich unter jeder Tour */}
        {dropdownState[tour.id] && (
          <DroppableContainer id={`tour-${tour.id}`} style={{ minHeight: '100px', padding: '10px', backgroundColor: '#f9f9f9', marginTop: '5px' }}>
            <SortableContext items={(containers[`tour-${tour.id}`] || []).filter(item => item).map(item => item.uniqueId)} strategy={verticalListSortingStrategy}>
              {(containers[`tour-${tour.id}`] || []).filter(item => item).map(item => (
                <SortableItem key={item.uniqueId} item={item} onClick={openEditMarkerModal} draggable={true} />
              ))}
            </SortableContext>
          </DroppableContainer>
        )}
      </div>
    ))}
  </div>
)}

      </DroppableContainer>
    );
  })}
</div>

        </div>
      </DndContext>
      {creatingTourFor && (
        <Modal
          isOpen={true}
          onRequestClose={() => setCreatingTourFor(null)}
          contentLabel="Neue Tour anlegen"
          ariaHideApp={false}
          style={modalStyles}
        >
          <CreateTourForm
            vehicle={creatingTourFor}
            selectedDate={selectedDate}
            drivers={drivers}
            onTourCreated={handleTourCreated}
            onCancel={() => setCreatingTourFor(null)}
          />
        </Modal>
      )}
      {selectedTransport && (
        <Modal isOpen={true} onRequestClose={() => setEditingMarker(null)} contentLabel="Transport Details" ariaHideApp={false}>
          <h2>Transport Details</h2>
          <p><strong>{selectedTransport.name}</strong></p>
          {selectedTransport.relevantType === 'delivery' ? (
            <>
              <p>Delivery: {selectedTransport.delivery} </p>
              <p>Delivery Location: {selectedTransport.operationLocationDelivery}</p>
            </>
          ) : (
            <>
              <p>Pickup: {selectedTransport.pickup}</p>
              <p>Pickup Location: {selectedTransport.operationLocationPickup}</p>
            </>
          )}
          {selectedTransport.transportNotes && <p>Notes: {selectedTransport.transportNotes}</p>}
          {selectedTransport.orderItem && selectedTransport.orderItem.item && <p>Gerät: {selectedTransport.orderItem.item.name}</p>}
          {selectedTransport.orderItem && selectedTransport.orderItem.order && (
            <>
              <p>Auftragsnummer: {selectedTransport.orderItem.order.orderNumber || 'Keine Angabe'}</p>
              <p>
                Kunde: {selectedTransport.orderItem.order.customer
                  ? selectedTransport.orderItem.order.customer.company
                  : 'Keine Angabe'}
              </p>
              <p>
                Ansprechpartner vor Ort: {selectedTransport.orderItem.order.lwOperator
                  ? `${selectedTransport.orderItem.order.lwOperator.contactFirstName} ${selectedTransport.orderItem.order.lwOperator.contactLastName} , ${selectedTransport.orderItem.order.lwOperator.contactMobile} , ${selectedTransport.orderItem.order.lwOperator.contactPhone}`
                  : 'Nicht zugeordnet'}
              </p>
            </>
          )}
          <button onClick={() => setEditingMarker(selectedTransport)}>Bearbeiten</button>
          <button onClick={() => setSelectedTransport(null)}>Schließen</button>
        </Modal>
      )}
      {editingTour && (
  <Modal isOpen={true} onRequestClose={() => setEditingTour(null)} contentLabel="Tour Editieren" style={modalStyles}>
    <EditTourForm 
      tour={editingTour} 
      selectedDate={selectedDate}  // hier weiterreichen
      onClose={() => setEditingTour(null)} 
      onTourUpdated={handleTourUpdated}
      drivers={drivers}
      onTourDeleted={handleTourDeleted} 
    />
  </Modal>
      )}
      {editingMarker && (
        <EditMarkerDetailsModal
          marker={editingMarker}
          // nur die gerade sichtbaren/verfügbaren Fahrzeuge übergeben
          vehicles={vehicles.filter(vehicle => {
            if (vehicle.deactivated) return false;
            // Workshop-Block prüfen
            const ordersForVehicle = workshopOrders.filter(o => o.itemId === vehicle.id);
            if (ordersForVehicle.length === 0) return true;
            const sel = moment(selectedDate).startOf('day');
            return !ordersForVehicle.some(order => {
              const from = moment(order.blockedPeriodFrom).startOf('day');
              const to   = moment(order.blockedPeriodTo).endOf('day');
              return sel.isBetween(from, to, null, '[]');
            });
          })}
          toursByVehicle={tours}
          selectedDate={selectedDate}
          onClose={closeEditMarkerModal}
          onMarkerUpdated={handleMarkerUpdated}
        />
      )}
      {pendingAssignment && (
        <EditTimeModal
          pending={pendingAssignment}
          onClose={() => setPendingAssignment(null)}
          onTimeAssigned={handleTimeUpdated}
        />
      )}
    </div>
  );
};

export default TransportForm;
